import { isDefined } from '@sgme/fp';
import classNames from 'classnames';
import { useMinMaxText } from 'components/common-form/hooks/useMinMaxText';
import { Block } from 'components/secondaryMarket/shared/Blocks/Block';
import { AmountMode } from 'components/secondaryMarket/shared/components/AmountMode';
import { CustodiansSearchField } from 'components/secondaryMarket/shared/Custodians/CustodiansSearchField';
import type { TradeInformationProps } from 'components/secondaryMarket/shared/sections/SecondaryTradeInformation.connect';
import { NumberFormatterWithUnit } from 'components/shared/NumberFormatter/NumberFormatterWithUnit';
import { useIntl } from 'react-intl';

import { TradeWayButtons } from '../components/TradeWayButtons';
import {
  VerticalFormGroup,
  VerticalFormGroupInput,
  VerticalFormGroupLabel,
  VerticalFormGroupMessage,
} from '../VerticalFormGroup';

const fieldIds: string[] = ['quantity', 'cashAmount', 'tradeNotional', 'custodian'];

function QuantityLabel({ remaining }: { remaining: number | undefined }) {
  const { formatMessage } = useIntl();
  return (
    <div className="d-flex justify-content-between w-100">
      <span>{formatMessage({ id: 'secondaryMarket.tradeInformation.label.byQuantity' })}</span>
      <span>
        {isDefined(remaining) &&
          formatMessage({ id: 'secondaryMarket.tradeInformation.label.remaining' }, { remaining })}
      </span>
    </div>
  );
}

export function SecondaryTradeInformation({
  onQuantityChanged,
  onQuantityReset,
  onTradeWayChanged,
  onCustodianChanged,
  onCashAmountChanged,
  onCashAmountReset,
  className,
  currency,
  denomination,
  tradeWay,
  selectedCustodian,
  quantity,
  cashAmount,
  effectiveCashAmount,
  needCustodian,
  disabledTradeWays,
  tradeSizeInputMode = 'quantity',
  onTradeSizeInputModeChanged,
  isFrozen,
  minQuantity,
  maxQuantity,
  lot,
  children,
  remaining,
  isModification,
}: TradeInformationProps) {
  const { formatMessage } = useIntl();
  let unit = formatMessage({ id: 'secondaryMarket.tradeInformation.quantity.unit' });
  if (isDefined(lot)) {
    unit = formatMessage(
      {
        id: 'secondaryMarket.tradeInformation.quantity.unitWithLot',
      },
      { lot },
    );
  }

  const placeholder = useMinMaxText({ min: minQuantity, max: maxQuantity });

  return (
    <Block
      className={className}
      label="secondaryMarket.tradeInformation.label.tradeInformation"
      id="trade-information"
      fieldIds={fieldIds}
    >
      <div>
        <div className="row my-1" tabIndex={1}>
          <VerticalFormGroup
            inputId="tradeWay"
            label="secondaryMarket.tradeInformation.tradeWay.label"
            className="col-md-12"
          >
            <TradeWayButtons
              value={tradeWay}
              onValueChanged={onTradeWayChanged}
              disabledValues={disabledTradeWays}
              isDisabled={isFrozen || isModification}
              tabIndex={2}
              e2e="trade-way-buttons"
            />
          </VerticalFormGroup>
        </div>
        <div className="d-flex my-1 justify-content-between align-items-top flex-wrap">
          <div className="d-flex w-100">
            <div className="col">
              <VerticalFormGroupLabel inputId="quantity" labelNode={<QuantityLabel remaining={remaining} />} />
            </div>
            <div className="col-1">
              <VerticalFormGroupLabel />
            </div>
            <div className="col">
              <VerticalFormGroupLabel
                inputId="cashAmount"
                label="secondaryMarket.tradeInformation.label.byCashAmount"
              />
            </div>
          </div>
          <div className="d-flex w-100" tabIndex={4}>
            <div className="col">
              <VerticalFormGroupInput inputId="quantity">
                <AmountMode
                  inputMode={'quantity'}
                  onTradeSizeInputModeChanged={onTradeSizeInputModeChanged}
                  placeholder={placeholder}
                  onBlur={onQuantityChanged}
                  onFocus={onQuantityReset}
                  e2e="quantity"
                  incrementStep={1}
                  unit={unit}
                  value={quantity}
                  precision={0}
                  isDisabled={isFrozen}
                  tabIndex={tradeSizeInputMode === 'quantity' ? 4 : -1}
                  isModeActive={tradeSizeInputMode === 'quantity'}
                />
              </VerticalFormGroupInput>
            </div>
            <div className="col-1 text-center">
              <VerticalFormGroupInput>
                <button
                  className="btn btn-icon my-auto text-primary mx-auto"
                  onClick={onTradeSizeInputModeChanged}
                  disabled={isFrozen}
                >
                  <em className="icon icon-sm">sync_alt</em>
                </button>
              </VerticalFormGroupInput>
            </div>
            <div className="col">
              <VerticalFormGroupInput inputId="cashAmount">
                <AmountMode
                  inputMode={'amount'}
                  onTradeSizeInputModeChanged={onTradeSizeInputModeChanged}
                  value={effectiveCashAmount ?? cashAmount}
                  unit={currency}
                  e2e="cashAmount"
                  onBlur={onCashAmountChanged}
                  onFocus={onCashAmountReset}
                  incrementStep={1}
                  isDisabled={isFrozen}
                  decimalPadding={false}
                  precision={2}
                  tabIndex={tradeSizeInputMode === 'amount' ? 4 : -1}
                  isModeActive={tradeSizeInputMode === 'amount'}
                  className={classNames({
                    'is-highlighted-success': effectiveCashAmount !== undefined,
                  })}
                />
              </VerticalFormGroupInput>
            </div>
          </div>
          <div className="d-flex w-100">
            <div className="col">
              <VerticalFormGroupMessage inputId="quantity" />
            </div>
            <div className="col-1" />
            <div className="col">
              <VerticalFormGroupMessage inputId="cashAmount" />
            </div>
          </div>
        </div>
        <div className="row my-1">
          <VerticalFormGroup
            inputId="denomination"
            label="secondaryMarket.productSummary.label.denomination"
            className="col"
          >
            {currency !== undefined && denomination !== undefined && (
              <NumberFormatterWithUnit
                value={denomination}
                unit={currency}
                e2e="denomination"
                isReadOnly
                id="denomination"
                tabIndex={-1}
              />
            )}
          </VerticalFormGroup>
          {children}
        </div>

        {needCustodian && (
          <div className="row " tabIndex={5}>
            <VerticalFormGroup
              inputId="custodians"
              label="secondaryMarket.tradeInformation.label.custodian"
              className="col-12"
            >
              <CustodiansSearchField
                className="flex-grow-1 justify-content-start min-width-0"
                isDisabled={isFrozen}
                onCustodianChanged={onCustodianChanged}
                selectedCustodian={selectedCustodian}
              />
            </VerticalFormGroup>
          </div>
        )}
      </div>
    </Block>
  );
}
